import { useInclinicStore } from '@/store';
import { useCallback, useEffect, useMemo } from 'react';
import { useIsWelfare } from '@/app/_hooks/useIsWelfare';
import { posthogTracker } from '@/utils/trackingPostHog';
import { getLoggedPatientUsername } from '@/api/eltyAuth';
import { getLoggedPatient } from '@/api/patient';

// Flag to ensure patient is fetched only once per session
let isPatientFetched = false;

export const usePatient = () => {
  const {
    patient,
    isPatientLoading,
    patientLoggedUsername,
    setPatient,
    updatePatient,
    clearPatient,
    setPatientLoading,
    setPatientLoggedUsername,
  } = useInclinicStore();
  const isWelfare = useIsWelfare();

  const fetchPatient = useCallback(async () => {
    try {
      setPatientLoading(true);

      const [loggedPatient, loggedPatientUsername] = await Promise.all([getLoggedPatient(), getLoggedPatientUsername()]);

      if (!loggedPatient) {
        clearPatient();

        return;
      }

      posthogTracker.identify(loggedPatient.id);

      setPatient(loggedPatient);
      setPatientLoggedUsername(loggedPatientUsername);
    } catch (error) {
      console.error({ errorOnGetLoggedPatient: JSON.stringify(error) });
      clearPatient();
    } finally {
      setPatientLoading(false);
    }
  }, [setPatient, setPatientLoading, clearPatient, setPatientLoggedUsername]);

  useEffect(() => {
    if (isWelfare) {
      clearPatient();

      return;
    }

    if (isPatientFetched) return;

    fetchPatient();

    isPatientFetched = true;
  }, [isWelfare, fetchPatient, clearPatient]);

  const isEmailPatientLoggedUsername = useMemo(() => patientLoggedUsername?.includes('@'), [patientLoggedUsername]);

  const patientEmail = useMemo(() => {
    if (!!patientLoggedUsername && isEmailPatientLoggedUsername) {
      return patientLoggedUsername;
    }

    return patient?.patientEmails[0]?.address ?? '';
  }, [patient, isEmailPatientLoggedUsername, patientLoggedUsername]);

  const patientPhone = useMemo(() => {
    if (!!patientLoggedUsername && !isEmailPatientLoggedUsername) {
      return patientLoggedUsername;
    }

    return patient?.phones[0]?.phoneNumber ?? '';
  }, [patient, isEmailPatientLoggedUsername, patientLoggedUsername]);

  return {
    isPatientLoading,
    patient,
    patientEmail,
    patientPhone,
    setPatient,
    updatePatient,
    clearPatient,
    setPatientLoading,
  };
};
